import React, { useContext, useEffect, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import CurrencyFormat from "react-currency-format";
import axios from "axios";
import authHeader from "../../UserServices/auth-header";
import UserServices from "../../UserServices/UserServices";
import Swal from "sweetalert2";

const DetailedPackages = () => {
  const { state } = useContext(NoteState);
  const { branch_id } = state;

  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [groupedPackages, setGroupedPackages] = useState({});
  const [filteredPackages, setFilteredPackages] = useState({});
  
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedDuration, setSelectedDuration] = useState("");
  const [selectedTrainer, setSelectedTrainer] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(""); // "1" for active, "0" for inactive, "" for all
  const [selectedBranch, setSelectedBranch] = useState(branch_id || "");
  
  // Available options for filters
  const [categories, setCategories] = useState([]);
  const [durations, setDurations] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [branches, setBranches] = useState([]);
  
  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  const formatDuration = (days) => {
    if (!days || days === 0) return "N/A";
    const months = Math.round(days / 30);
    if (months === 0) return `${days} Day${days !== 1 ? 's' : ''}`;
    if (days % 30 === 0) {
      if (months === 1) return "Monthly";
      if (months === 3) return "3 Months";
      if (months === 6) return "6 Months";
      if (months === 12) return "Annual";
      return `${months} Month${months !== 1 ? 's' : ''}`;
    }
    return `${days} Day${days !== 1 ? 's' : ''} (${months} Month${months !== 1 ? 's' : ''})`;
  };

  const formatTime = (time) => {
    if (!time || time === "00:00:00" || time === "00:00") return "N/A";
    return time.substring(0, 5);
  };

  const getAllPackages = async (branchId = null) => {
    setLoading(true);
    try {
      const targetBranchId = branchId !== null ? branchId : (selectedBranch || branch_id);
      const params = {};
      if (targetBranchId) {
        params.branch_id = targetBranchId;
      }
      const response = await axios.get(
        process.env.REACT_APP_API_Link + "/packages/all-with-categories",
        {
          params: params,
          headers: authHeader(),
        }
      );

      if (response.status === 200) {
        const data = response.data.data || [];
        // Show all packages (active and inactive)
        setPackages(data);

        // Extract unique categories, durations, and trainers for filters
        const uniqueCategories = [...new Set(data.map(pkg => pkg.category_name).filter(Boolean))].sort();
        const uniqueDurations = [...new Set(data.map(pkg => formatDuration(pkg.duration)))].sort((a, b) => {
          const order = { "Monthly": 1, "3 Months": 2, "6 Months": 3, "Annual": 4 };
          const aOrder = order[a] || 99;
          const bOrder = order[b] || 99;
          if (aOrder !== bOrder) return aOrder - bOrder;
          return a.localeCompare(b);
        });
        const uniqueTrainers = [...new Set(
          data
            .map(pkg => {
              const trainerName = (pkg.user_first_name || pkg.user_last_name)
                ? `${pkg.user_first_name || ""} ${pkg.user_last_name || ""}`.trim()
                : null;
              return trainerName;
            })
            .filter(Boolean)
        )].sort();
        
        setCategories(uniqueCategories);
        setDurations(uniqueDurations);
        setTrainers(uniqueTrainers);

        // Group packages: First by category (and trainer for PT), then by duration within each category
        const grouped = {};
        data.forEach((pkg) => {
          let categoryKey = "";
          const categoryName = pkg.category_name || "Uncategorized";
          const trainerName = (pkg.user_first_name || pkg.user_last_name)
            ? `${pkg.user_first_name || ""} ${pkg.user_last_name || ""}`.trim()
            : null;

          // For Personal Trainer packages (category = 2), group by trainer name
          if (pkg.category === 2 || pkg.category === "2") {
            categoryKey = trainerName ? `${categoryName} - ${trainerName}` : `${categoryName} - No Trainer`;
          } else {
            categoryKey = categoryName;
          }

          // Get duration for sub-grouping
          const durationKey = formatDuration(pkg.duration);
          
          if (!grouped[categoryKey]) {
            grouped[categoryKey] = {};
          }
          if (!grouped[categoryKey][durationKey]) {
            grouped[categoryKey][durationKey] = [];
          }
          grouped[categoryKey][durationKey].push(pkg);
        });
        setGroupedPackages(grouped);
        setFilteredPackages(grouped);
      }
    } catch (err) {
      console.error("Error fetching packages:", err);
      setPackages([]);
      setGroupedPackages({});
    } finally {
      setLoading(false);
    }
  };

  // Fetch branches on component mount
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await UserServices.branchName();
        if (response.status === 200 && response.data.data) {
          const branchesList = response.data.data.map(b => ({
            id: String(b.id),
            name: b.name
          }));
          setBranches(branchesList);
        }
      } catch (err) {
        console.error("Error fetching branches:", err);
      }
    };
    fetchBranches();
  }, []);

  useEffect(() => {
    // Fetch packages when branch selection changes
    // If no branch selected, use current branch_id, or fetch all if API supports it
    const targetBranchId = selectedBranch || branch_id;
    getAllPackages(targetBranchId || null);
  }, [selectedBranch, branch_id]);

  // Apply filters when they change
  useEffect(() => {
    let filtered = { ...groupedPackages };

    // Filter by category
    if (selectedCategory) {
      Object.keys(filtered).forEach((categoryKey) => {
        if (!categoryKey.includes(selectedCategory)) {
          delete filtered[categoryKey];
        }
      });
    }

    // Filter by duration
    if (selectedDuration) {
      Object.keys(filtered).forEach((categoryKey) => {
        Object.keys(filtered[categoryKey]).forEach((durationKey) => {
          if (durationKey !== selectedDuration) {
            delete filtered[categoryKey][durationKey];
          }
        });
        // Remove category if no durations left
        if (Object.keys(filtered[categoryKey]).length === 0) {
          delete filtered[categoryKey];
        }
      });
    }

    // Filter by trainer name
    if (selectedTrainer) {
      Object.keys(filtered).forEach((categoryKey) => {
        if (!categoryKey.includes(selectedTrainer)) {
          delete filtered[categoryKey];
        }
      });
    }

    // Filter by status (active/inactive)
    if (selectedStatus !== "") {
      Object.keys(filtered).forEach((categoryKey) => {
        Object.keys(filtered[categoryKey]).forEach((durationKey) => {
          filtered[categoryKey][durationKey] = filtered[categoryKey][durationKey].filter((pkg) => {
            const pkgStatus = String(pkg.status || "");
            return pkgStatus === selectedStatus;
          });
        });
        // Remove empty duration groups
        Object.keys(filtered[categoryKey]).forEach((durationKey) => {
          if (filtered[categoryKey][durationKey].length === 0) {
            delete filtered[categoryKey][durationKey];
          }
        });
        // Remove category if no durations left
        if (Object.keys(filtered[categoryKey]).length === 0) {
          delete filtered[categoryKey];
        }
      });
    }

    setFilteredPackages(filtered);
  }, [selectedCategory, selectedDuration, selectedTrainer, selectedStatus, groupedPackages]);

  // Handle edit button click
  const handleEditClick = (pkg) => {
    setSelectedPackage(pkg);
    const packageName = pkg.package_name || pkg.name || "";
    // Use the original package's branch_id, not the current branch_id
    const originalBranchId = pkg.branch_id || branch_id;
    const formData = {
      branch_id: originalBranchId, // Keep the original branch_id
      package_name: packageName,
      duration: String(pkg.duration || ""),
      price: String(pkg.price || ""),
      session_count: String(pkg.session_count || ""),
      status: String(pkg.status || "1"),
    };
    setEditFormData(formData);
    setShowEditModal(true);
  };

  // Handle activate package
  const handleActivatePackage = async (pkg) => {
    try {
      setLoading(true);
      const response = await UserServices.activePackage(pkg.id);
      // Backend returns 204 (No Content) for successful activation, or 200 with status: true
      if ((response.status === 204) || (response.status === 200 && response.data?.status)) {
        Swal.fire("Activated!", "Package has been activated.", "success");
        const targetBranchId = selectedBranch || branch_id;
        getAllPackages(targetBranchId);
      } else {
        Swal.fire("Error!", response.data?.message || "Failed to activate package.", "error");
      }
    } catch (err) {
      console.error("Error activating package:", err);
      Swal.fire("Error!", err.response?.data?.message || "Failed to activate package.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle deactivate package
  const handleDeactivatePackage = async (pkg) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to deactivate package "${pkg.package_name || pkg.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, deactivate it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await UserServices.inactivePackage(pkg.id);
        // Backend returns 204 (No Content) for successful deactivation, or 200 with status: true
        if ((response.status === 204) || (response.status === 200 && response.data?.status)) {
          Swal.fire("Deactivated!", "Package has been deactivated.", "success");
          const targetBranchId = selectedBranch || branch_id;
          getAllPackages(targetBranchId);
        } else {
          Swal.fire("Error!", response.data?.message || "Failed to deactivate package.", "error");
        }
      } catch (err) {
        console.error("Error deactivating package:", err);
        Swal.fire("Error!", err.response?.data?.message || "Failed to deactivate package.", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle delete button click
  const handleDeleteClick = async (pkg) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete package "${pkg.package_name || pkg.name}"? This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await UserServices.deletePackages(pkg.id);
        // Backend returns 204 (No Content) for successful deletion, or 200 with status: true
        if ((response.status === 204) || (response.status === 200 && response.data?.status)) {
          Swal.fire("Deleted!", "Package has been deleted.", "success");
          const targetBranchId = selectedBranch || branch_id;
          getAllPackages(targetBranchId);
        } else {
          Swal.fire("Error!", response.data?.message || "Failed to delete package.", "error");
        }
      } catch (err) {
        console.error("Error deleting package:", err);
        Swal.fire("Error!", err.response?.data?.message || "Failed to delete package.", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle edit form submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPackage) return;

    try {
      setEditLoading(true);
      // Always use the original package's branch_id to keep it unchanged
      const originalBranchId = selectedPackage.branch_id || editFormData.branch_id || branch_id;
      const updateData = {
        branch_id: originalBranchId, // Keep the original branch_id unchanged
        package_name: editFormData.package_name || selectedPackage.package_name || selectedPackage.name,
        duration: editFormData.duration || selectedPackage.duration,
        price: editFormData.price || selectedPackage.price,
        session_count: editFormData.session_count || selectedPackage.session_count || "0",
        status: editFormData.status || selectedPackage.status || "1",
      };
      
      const response = await UserServices.UpdatePackages(updateData, selectedPackage.id);
      // Backend returns 204 (No Content) for successful updates, or 200 with status: true
      if ((response.status === 204) || (response.status === 200 && response.data?.status)) {
        Swal.fire("Success!", "Package has been updated.", "success");
        setShowEditModal(false);
        setSelectedPackage(null);
        setEditFormData({});
        const targetBranchId = selectedBranch || branch_id;
        getAllPackages(targetBranchId);
      } else {
        Swal.fire("Error!", response.data?.message || "Failed to update package.", "error");
      }
    } catch (err) {
      console.error("Error updating package:", err);
      Swal.fire("Error!", err.response?.data?.message || "Failed to update package.", "error");
    } finally {
      setEditLoading(false);
    }
  };

  const PackageCard = ({ pkg }) => {
    const trainerName = pkg.user_first_name || pkg.user_last_name
      ? `${pkg.user_first_name || ""} ${pkg.user_last_name || ""}`.trim()
      : null;
    const branchName = pkg.branches_name || pkg.branch_name || "";

    return (
      <div className="col-6 col-sm-4 col-md-4 col-lg-3 col-xl-3 mb-3">
        <div className="card shadow-sm border" style={{height: 'auto'}}>
          <div className="card-body p-2" style={{padding: '0.75rem'}}>
            {branchName && (
              <div className="mb-2">
                <span className="badge bg-info text-dark" style={{fontSize: '0.7rem', fontWeight: '600'}}>
                  <i className="bi bi-building me-1"></i>
                  {branchName}
                </span>
              </div>
            )}
            <div className="d-flex justify-content-between align-items-start mb-1">
              <h6 className="mb-0 fw-bold small" style={{fontSize: '0.8rem', lineHeight: '1.2', wordBreak: 'break-word', flex: '1', minWidth: 0}} title={pkg.package_name}>
                {pkg.package_name}
              </h6>
              <div className="d-flex gap-1">
                <button
                  className="btn btn-sm btn-outline-primary p-1"
                  style={{fontSize: '0.7rem', padding: '2px 6px'}}
                  onClick={() => handleEditClick(pkg)}
                  title="Edit Package"
                >
                  <i className="bi bi-pencil"></i>
                </button>
                {pkg.status === "1" || pkg.status === 1 ? (
                  <button
                    className="btn btn-sm btn-outline-warning p-1"
                    style={{fontSize: '0.7rem', padding: '2px 6px'}}
                    onClick={() => handleDeactivatePackage(pkg)}
                    title="Deactivate Package"
                  >
                    <i className="bi bi-pause-circle"></i>
                  </button>
                ) : (
                  <button
                    className="btn btn-sm btn-outline-success p-1"
                    style={{fontSize: '0.7rem', padding: '2px 6px'}}
                    onClick={() => handleActivatePackage(pkg)}
                    title="Activate Package"
                  >
                    <i className="bi bi-play-circle"></i>
                  </button>
                )}
                <button
                  className="btn btn-sm btn-outline-danger p-1"
                  style={{fontSize: '0.7rem', padding: '2px 6px'}}
                  onClick={() => handleDeleteClick(pkg)}
                  title="Delete Package"
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            </div>
            <div className="mb-1">
              {(pkg.status === "1" || pkg.status === 1) ? (
                <span className="badge bg-success small">Active</span>
              ) : (
                <span className="badge bg-warning small">Inactive</span>
              )}
            </div>
            <div className="mb-1">
              <strong className="text-primary" style={{fontSize: '0.75rem'}}>
                <CurrencyFormat
                  value={pkg.price || 0}
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={"Rs "}
                  suffix={"/-"}
                  thousandSpacing={"3"}
                />
              </strong>
            </div>
            <div className="mb-1" style={{fontSize: '0.7rem'}}>
              <i className="bi bi-calendar3 me-1"></i>
              <span className="text-muted">{formatDuration(pkg.duration)}</span>
            </div>
            {trainerName && (
              <div className="mb-1" style={{fontSize: '0.7rem'}}>
                <i className="bi bi-person me-1"></i>
                <span className="text-muted text-truncate d-block" title={trainerName}>{trainerName}</span>
              </div>
            )}
            {pkg.session_count > 0 && (
              <div className="mb-1" style={{fontSize: '0.7rem'}}>
                <i className="bi bi-check-circle me-1"></i>
                <span className="text-muted">{pkg.session_count} Sessions</span>
              </div>
            )}
            {pkg.membership_types_name && (
              <div className="mb-1" style={{fontSize: '0.7rem'}}>
                <i className="bi bi-tag me-1"></i>
                <span className="text-muted text-truncate d-block" title={pkg.membership_types_name}>{pkg.membership_types_name}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const DurationSection = ({ durationName, packages }) => {
    return (
      <div className="mb-3">
        <h6 className="text-secondary mb-2" style={{fontSize: '0.9rem'}}>
          <i className="bi bi-clock me-1"></i>
          {durationName} <span className="badge bg-secondary">{packages.length}</span>
        </h6>
        <div className="row">
          {packages.map((pkg) => (
            <PackageCard key={pkg.id} pkg={pkg} />
          ))}
        </div>
      </div>
    );
  };

  const CategorySection = ({ categoryName, durationGroups }) => {
    const totalPackages = Object.values(durationGroups).reduce((sum, pkgs) => sum + pkgs.length, 0);
    const sortedDurations = Object.keys(durationGroups).sort((a, b) => {
      // Sort durations logically: Monthly, 3 Months, 6 Months, Annual, then by days
      const order = { "Monthly": 1, "3 Months": 2, "6 Months": 3, "Annual": 4 };
      const aOrder = order[a] || 99;
      const bOrder = order[b] || 99;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.localeCompare(b);
    });

    return (
      <div className="mb-4">
        <div className="card mb-3 shadow-sm">
          <div className="card-header bg-primary text-white py-2">
            <h5 className="mb-0" style={{fontSize: '1rem'}}>
              <i className="bi bi-tag-fill me-2"></i>
              {categoryName} <span className="badge bg-light text-dark ms-2">{totalPackages}</span>
            </h5>
          </div>
        </div>
        <div className="ms-3">
          {sortedDurations.map((durationName) => (
            <DurationSection
              key={durationName}
              durationName={durationName}
              packages={durationGroups[durationName]}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="card">
            <div className="card-header py-3">
              <h5 className="mb-0 font-20">Detailed Packages</h5>
            </div>
            <div className="card-body">
              {/* Filters Section */}
              <div className="row mb-4">
                <div className="col-12 col-md-3 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Filter by Branch</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedBranch}
                      onChange={(e) => {
                        setSelectedBranch(e.target.value);
                        // Reset other filters when branch changes
                        setSelectedCategory("");
                        setSelectedDuration("");
                        setSelectedTrainer("");
                        setSelectedStatus("");
                      }}
                    >
                      <option value="">All Branches</option>
                      {branches.map((branch) => (
                        <option key={branch.id} value={branch.id}>{branch.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-3 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Filter by Status</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                      <option value="">All Status</option>
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-3 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Filter by Category</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="">All Categories</option>
                      {categories.map((cat, idx) => (
                        <option key={idx} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-3 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Filter by Duration</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedDuration}
                      onChange={(e) => setSelectedDuration(e.target.value)}
                    >
                      <option value="">All Durations</option>
                      {durations.map((dur, idx) => (
                        <option key={idx} value={dur}>{dur}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-3 mb-3 mb-md-0">
                  <div className="form-group">
                    <label className="form-label fw-bold">Filter by Trainer</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedTrainer}
                      onChange={(e) => setSelectedTrainer(e.target.value)}
                    >
                      <option value="">All Trainers</option>
                      {trainers.map((trainer, idx) => (
                        <option key={idx} value={trainer}>{trainer}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {(selectedCategory || selectedDuration || selectedTrainer || selectedStatus || selectedBranch) && (
                  <div className="col-12 mt-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setSelectedCategory("");
                        setSelectedDuration("");
                        setSelectedTrainer("");
                        setSelectedStatus("");
                        setSelectedBranch(branch_id || "");
                      }}
                    >
                      <i className="bi bi-x-circle me-1"></i> Clear All Filters
                    </button>
                  </div>
                )}
              </div>
              <hr />
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : Object.keys(filteredPackages).length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">No packages found matching your filters</p>
                </div>
              ) : (
                <>
                  {Object.keys(filteredPackages)
                    .sort()
                    .map((categoryName) => (
                      <CategorySection
                        key={categoryName}
                        categoryName={categoryName}
                        durationGroups={filteredPackages[categoryName]}
                      />
                    ))}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      <div className={`loader ${loading ? "in" : ""}`}>
        <div className="spinner-border main-spin"></div>
      </div>

      {/* Edit Package Modal */}
      {showEditModal && (
        <>
          <style>
            {`
              .edit-package-modal-wrapper {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 1050;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: rgba(0, 0, 0, 0.5);
              }
              .edit-package-modal-content {
                background: white;
                border-radius: 8px;
                width: 90%;
                max-width: 900px;
                max-height: 90vh;
                overflow-y: auto;
                z-index: 1051;
                position: relative;
              }
              .edit-package-modal-content .form-control,
              .edit-package-modal-content .form-select {
                background-color: #ffffff !important;
                background-image: none !important;
                color: #212529 !important;
                cursor: text !important;
                -webkit-appearance: none !important;
                -moz-appearance: none !important;
                appearance: none !important;
              }
              .edit-package-modal-content .form-select {
                cursor: pointer !important;
              }
              .edit-package-modal-content .form-control:focus,
              .edit-package-modal-content .form-select:focus {
                background-color: #ffffff !important;
                color: #212529 !important;
                border-color: #86b7fe !important;
                box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25) !important;
                outline: none !important;
              }
              .edit-package-modal-content input,
              .edit-package-modal-content select,
              .edit-package-modal-content textarea {
                background-color: #ffffff !important;
                color: #212529 !important;
              }
              .edit-package-modal-content input:disabled,
              .edit-package-modal-content select:disabled,
              .edit-package-modal-content textarea:disabled {
                background-color: #ffffff !important;
                color: #212529 !important;
                opacity: 1 !important;
              }
            `}
          </style>
          <div 
            className="edit-package-modal-wrapper"
            onClick={(e) => {
              // Only close if clicking the backdrop (wrapper itself)
              if (e.target.classList.contains('edit-package-modal-wrapper')) {
                setShowEditModal(false);
                setSelectedPackage(null);
                setEditFormData({});
              }
            }}
          >
            <div 
              className="edit-package-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header" style={{ borderBottom: '1px solid #dee2e6', padding: '1rem' }}>
                <h5 className="modal-title">Edit Package</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedPackage(null);
                    setEditFormData({});
                  }}
                  style={{ margin: 0 }}
                ></button>
              </div>
              <div className="modal-body" style={{ padding: '1.5rem' }}>
                <form onSubmit={handleEditSubmit} onClick={(e) => e.stopPropagation()}>
                <div className="row">
                    <div className="col-12 col-md-6 mb-3">
                      <label className="form-label fw-bold">Package Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editFormData.package_name || ""}
                        onChange={(e) => {
                          e.stopPropagation();
                          setEditFormData(prev => ({ ...prev, package_name: e.target.value }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </div>
                    <div className="col-12 col-md-6 mb-3">
                      <label className="form-label fw-bold">Price</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editFormData.price || ""}
                        onChange={(e) => {
                          e.stopPropagation();
                          setEditFormData(prev => ({ ...prev, price: e.target.value }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        min="0"
                      />
                    </div>
                    <div className="col-12 col-md-6 mb-3">
                      <label className="form-label fw-bold">Duration (Days)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editFormData.duration || ""}
                        onChange={(e) => {
                          e.stopPropagation();
                          setEditFormData(prev => ({ ...prev, duration: e.target.value }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        min="1"
                      />
                    </div>
                    <div className="col-12 col-md-6 mb-3">
                      <label className="form-label fw-bold">Session Count</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editFormData.session_count || ""}
                        onChange={(e) => {
                          e.stopPropagation();
                          setEditFormData(prev => ({ ...prev, session_count: e.target.value }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        min="0"
                      />
                    </div>
                    <div className="col-12 col-md-6 mb-3">
                      <label className="form-label fw-bold">Status</label>
                      <select
                        className="form-select"
                        value={editFormData.status || "1"}
                        onChange={(e) => {
                          e.stopPropagation();
                          setEditFormData(prev => ({ ...prev, status: e.target.value }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer" style={{ borderTop: '1px solid #dee2e6', padding: '1rem', marginTop: '1rem' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowEditModal(false);
                        setSelectedPackage(null);
                        setEditFormData({});
                      }}
                      disabled={editLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={editLoading}
                    >
                      {editLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Updating...
                        </>
                      ) : (
                        "Update Package"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DetailedPackages;

